import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Layout, Package, Search, Plus, TrendingUp, Globe,
  Activity, Users, Eye, Clock, BarChart2, RefreshCw, Wifi, WifiOff,
  AlertCircle, CheckCircle2, LogIn
} from 'lucide-react';
import { blogApi, productsApi, seoApi } from '../api/cms';
import { defaultArticles, defaultProducts, defaultSeoPages } from '../api/defaults';
import type { BlogArticle, Product, SeoPage } from '../types';

/* ─── Tipuri trafic ─────────────────────────────────────────────────────── */
interface TrafficPoint { label: string; visitors: number; pageviews: number }
interface TrafficSnapshot {
  live: number;
  today: TrafficPoint[];
  week:  TrafficPoint[];
  month: TrafficPoint[];
  year:  TrafficPoint[];
  totals: { visitors: number; pageviews: number; avgSession: string; bounceRate: string };
}
type TrafficPeriod = 'today' | 'week' | 'month' | 'year';
type ConnectionStatus = 'idle' | 'connecting' | 'live' | 'offline';

/* ─── Google Analytics Data API v1 ─────────────────────────────────────── */
const GA4_DATA_BASE = 'https://analyticsdata.googleapis.com/v1beta';

// Formatează o dată ca YYYY-MM-DD
function fmtDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

// Citește settings din PHP sau fallback la defaults
async function loadSettings(): Promise<{ propertyId: string; clientId: string }> {
  try {
    const r = await fetch('/php-backend/cms-api/settings.php');
    if (r.ok) {
      const d = await r.json();
      const s = d.settings || d;
      return {
        propertyId: s.googleApi?.propertyId || '461287871',
        clientId:   s.googleApi?.clientId   || '185237368738-tfmn55decj9hjrr0im5gspvuv79iktsl.apps.googleusercontent.com',
      };
    }
  } catch { /* fallback */ }
  // Fallback hard-coded (credentialele reale)
  return {
    propertyId: '461287871',
    clientId:   '185237368738-tfmn55decj9hjrr0im5gspvuv79iktsl.apps.googleusercontent.com',
  };
}

// Incarca Google Identity Services script
function loadGisScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).google?.accounts?.oauth2) { resolve(); return; }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('GIS load failed'));
    document.head.appendChild(s);
  });
}

// Solicita token OAuth2 via popup Google
async function requestOAuthToken(clientId: string, scopes: string): Promise<string> {
  await loadGisScript();
  return new Promise((resolve, reject) => {
    const client = (window as any).google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: scopes,
      callback: (response: any) => {
        if (response.error) { reject(new Error(response.error)); return; }
        resolve(response.access_token);
      },
    });
    client.requestAccessToken({ prompt: '' }); // '' = silent daca exista token; 'consent' = forteaza
  });
}

// Apel GA4 Data API
async function ga4RunReport(
  propertyId: string,
  token: string,
  body: object
): Promise<any> {
  const r = await fetch(
    `${GA4_DATA_BASE}/properties/${propertyId}:runReport`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err?.error?.message || `HTTP ${r.status}`);
  }
  return r.json();
}

// Apel GA4 Realtime
async function ga4RunRealtime(propertyId: string, token: string): Promise<number> {
  const r = await fetch(
    `${GA4_DATA_BASE}/properties/${propertyId}:runRealtimeReport`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        metrics: [{ name: 'activeUsers' }],
      }),
    }
  );
  if (!r.ok) return 0;
  const d = await r.json();
  return parseInt(d.rows?.[0]?.metricValues?.[0]?.value || '0', 10);
}

// Parseaza un raport GA4 in TrafficPoint[]
function parseReport(data: any, labelField: 'date' | 'hour' | 'month', labelFn: (v: string) => string): TrafficPoint[] {
  if (!data?.rows) return [];
  return data.rows.map((row: any) => {
    const label = labelFn(row.dimensionValues?.[0]?.value || '');
    const visitors  = parseInt(row.metricValues?.[0]?.value || '0', 10);
    const pageviews = parseInt(row.metricValues?.[1]?.value || '0', 10);
    return { label, visitors, pageviews };
  });
}

// Construieste TrafficSnapshot complet din GA4
async function fetchGA4Traffic(propertyId: string, token: string): Promise<TrafficSnapshot> {
  const now    = new Date();
  const today  = fmtDate(now);
  const wStart = fmtDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay()));
  const mStart = fmtDate(new Date(now.getFullYear(), now.getMonth(), 1));
  const yStart = fmtDate(new Date(now.getFullYear(), 0, 1));

  const DIMS_DATE  = [{ name: 'date' }];
  const DIMS_HOUR  = [{ name: 'hour' }];
  const DIMS_MONTH = [{ name: 'yearMonth' }];
  const METRICS    = [{ name: 'activeUsers' }, { name: 'screenPageViews' }];
  const METRICS_TOT = [
    { name: 'activeUsers' },
    { name: 'screenPageViews' },
    { name: 'averageSessionDuration' },
    { name: 'bounceRate' },
  ];

  const [rToday, rWeek, rMonth, rYear, rTotals, liveUsers] = await Promise.all([
    ga4RunReport(propertyId, token, { dateRanges: [{ startDate: today, endDate: today }], dimensions: DIMS_HOUR, metrics: METRICS, orderBys: [{ dimension: { dimensionName: 'hour' }, desc: false }] }),
    ga4RunReport(propertyId, token, { dateRanges: [{ startDate: wStart, endDate: today }], dimensions: DIMS_DATE, metrics: METRICS, orderBys: [{ dimension: { dimensionName: 'date' }, desc: false }] }),
    ga4RunReport(propertyId, token, { dateRanges: [{ startDate: mStart, endDate: today }], dimensions: DIMS_DATE, metrics: METRICS, orderBys: [{ dimension: { dimensionName: 'date' }, desc: false }] }),
    ga4RunReport(propertyId, token, { dateRanges: [{ startDate: yStart, endDate: today }], dimensions: DIMS_MONTH, metrics: METRICS, orderBys: [{ dimension: { dimensionName: 'yearMonth' }, desc: false }] }),
    ga4RunReport(propertyId, token, { dateRanges: [{ startDate: '30daysAgo', endDate: today }], dimensions: [], metrics: METRICS_TOT }),
    ga4RunRealtime(propertyId, token),
  ]);

  const dayNames = ['Dum','Lun','Mar','Mie','Joi','Vin','Sâm'];
  const monthNames = ['Ian','Feb','Mar','Apr','Mai','Iun','Iul','Aug','Sep','Oct','Nov','Dec'];

  const todayPts = parseReport(rToday, 'hour', h => `${h.padStart(2,'0')}:00`);
  const weekPts  = parseReport(rWeek,  'date', d => {
    const dt = new Date(d.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
    return dayNames[dt.getDay()];
  });
  const monthPts = parseReport(rMonth, 'date', d => {
    const day = d.replace(/(\d{4})(\d{2})(\d{2})/, '$3');
    return String(parseInt(day, 10));
  });
  const yearPts  = parseReport(rYear, 'month', d => {
    const m = parseInt(d.slice(4), 10) - 1;
    return monthNames[m] || d;
  });

  // Totale 30 zile
  const totRow = rTotals?.rows?.[0];
  const totVisitors  = parseInt(totRow?.metricValues?.[0]?.value || '0', 10);
  const totViews     = parseInt(totRow?.metricValues?.[1]?.value || '0', 10);
  const avgSec       = parseFloat(totRow?.metricValues?.[2]?.value || '0');
  const avgMin       = Math.floor(avgSec / 60);
  const avgSecRem    = Math.floor(avgSec % 60);
  const bounceRaw    = parseFloat(totRow?.metricValues?.[3]?.value || '0');

  return {
    live: liveUsers,
    today: todayPts,
    week:  weekPts,
    month: monthPts,
    year:  yearPts,
    totals: {
      visitors:   totVisitors,
      pageviews:  totViews,
      avgSession: `${avgMin}:${String(avgSecRem).padStart(2,'0')}`,
      bounceRate: `${(bounceRaw * 100).toFixed(1)}%`,
    },
  };
}

/* ─── Generator date demo fallback ──────────────────────────────────────── */
function generateDemoData(): TrafficSnapshot {
  const rng = (min: number, max: number, seed: number) => {
    const x = Math.sin(seed * 9301 + 49297) * 233280;
    return Math.floor(min + (x - Math.floor(x)) * (max - min));
  };
  const now = new Date();
  const hour = now.getHours();
  const dayIdx = now.getDay();
  const today = Array.from({ length: hour + 1 }, (_, h) => ({
    label: `${String(h).padStart(2,'0')}:00`,
    visitors: rng(8, h >= 9 && h <= 18 ? 85 : 22, h + 10),
    pageviews: rng(12, h >= 9 && h <= 18 ? 180 : 45, h + 20),
  }));
  const dayNames = ['Dum','Lun','Mar','Mie','Joi','Vin','Sâm'];
  const week = Array.from({ length: dayIdx + 1 }, (_, d) => ({
    label: dayNames[d], visitors: rng(120, d < 5 ? 650 : 280, d + 30),
    pageviews: rng(280, d < 5 ? 1400 : 620, d + 40),
  }));
  const dayOfMonth = now.getDate();
  const month = Array.from({ length: dayOfMonth }, (_, d) => ({
    label: `${d+1}`, visitors: rng(90, 580, d + 50), pageviews: rng(190, 1300, d + 60),
  }));
  const monthNames = ['Ian','Feb','Mar','Apr','Mai','Iun','Iul','Aug','Sep','Oct','Nov','Dec'];
  const year = Array.from({ length: now.getMonth() + 1 }, (_, m) => ({
    label: monthNames[m], visitors: rng(2400, 14800, m + 70), pageviews: rng(5500, 32000, m + 80),
  }));
  return {
    live: rng(1, 8, Date.now() % 999), today, week, month, year,
    totals: {
      visitors: month.reduce((s, p) => s + p.visitors, 0),
      pageviews: month.reduce((s, p) => s + p.pageviews, 0),
      avgSession: `${rng(1,4,91)}:${String(rng(10,59,92)).padStart(2,'0')}`,
      bounceRate: `${rng(38,62,93)}%`,
    },
  };
}

/* ─── Mini bar chart ─────────────────────────────────────────────────────── */
function MiniBarChart({ data, colorVisitors = '#4ab8ff', colorViews = '#8b5cf6', showBoth = true }: {
  data: TrafficPoint[]; colorVisitors?: string; colorViews?: string; showBoth?: boolean;
}) {
  const maxV = Math.max(...data.map(d => Math.max(d.visitors, d.pageviews)), 1);
  const barW = Math.max(3, Math.min(14, Math.floor(280 / (data.length * (showBoth ? 2.3 : 1.3)))));
  const gap  = Math.max(1, Math.floor(barW * 0.3));
  const totalW = data.length * (showBoth ? (barW * 2 + gap * 2) : (barW + gap)) + gap;
  const h = 64;
  return (
    <svg viewBox={`0 0 ${totalW} ${h}`} className="w-full" style={{ height: h }}>
      {data.map((d, i) => {
        const hV = Math.max(2, (d.visitors  / maxV) * (h - 6));
        const hP = Math.max(2, (d.pageviews / maxV) * (h - 6));
        const xBase = i * (showBoth ? (barW * 2 + gap * 2) : (barW + gap)) + gap;
        return (
          <g key={i}>
            <rect x={xBase} y={h - hV} width={barW} height={hV} fill={colorVisitors} rx={2} opacity={0.75}/>
            {showBoth && <rect x={xBase + barW + gap} y={h - hP} width={barW} height={hP} fill={colorViews} rx={2} opacity={0.65}/>}
          </g>
        );
      })}
    </svg>
  );
}

/* ─── Live dot pulse ─────────────────────────────────────────────────────── */
function LiveDot() {
  return (
    <span className="relative flex h-2.5 w-2.5">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"/>
      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400"/>
    </span>
  );
}

/* ─── Connection Status Badge ────────────────────────────────────────────── */
function ConnectionBadge({ status, onConnect, errorMsg }: {
  status: ConnectionStatus;
  onConnect: () => void;
  errorMsg?: string;
}) {
  if (status === 'live') return (
    <div className="flex items-center gap-1.5">
      <CheckCircle2 size={11} style={{ color: '#00ff88', filter: 'drop-shadow(0 0 6px #00ff88)' }} />
      <span className="text-xs font-bold" style={{ color: '#00ff88', textShadow: '0 0 8px #00ff88, 0 0 16px #00ff88' }}>
        Connected: LIVE
      </span>
    </div>
  );

  if (status === 'offline') return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-1.5">
        <WifiOff size={11} style={{ color: '#ff4444', filter: 'drop-shadow(0 0 6px #ff4444)' }} />
        <span className="text-xs font-bold" style={{ color: '#ff4444', textShadow: '0 0 8px #ff4444, 0 0 16px #ff4444' }}>
          Connected: OffLine
        </span>
      </div>
      {errorMsg && <span className="text-[10px] text-red-400/60 max-w-[200px] text-right leading-tight">{errorMsg}</span>}
      <button onClick={onConnect}
        className="flex items-center gap-1 text-[10px] text-[#4ab8ff] hover:underline mt-0.5">
        <LogIn size={9} /> Reconectare GA4
      </button>
    </div>
  );

  if (status === 'connecting') return (
    <div className="flex items-center gap-1.5">
      <RefreshCw size={11} className="animate-spin text-[#4ab8ff]" />
      <span className="text-xs text-[#4ab8ff]">Se conectează GA4...</span>
    </div>
  );

  // idle
  return (
    <button onClick={onConnect}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs border border-[#295CA8]/40 text-[#4ab8ff] hover:bg-[#295CA8]/15 transition-all">
      <LogIn size={11} /> Conectare GA4
    </button>
  );
}

/* ─── Dashboard ─────────────────────────────────────────────────────────── */
export default function DashboardPage() {
  const [articles,   setArticles]   = useState<BlogArticle[]>([]);
  const [products,   setProducts]   = useState<Product[]>([]);
  const [seoPages,   setSeoPages]   = useState<SeoPage[]>([]);
  const [traffic,    setTraffic]    = useState<TrafficSnapshot>(() => generateDemoData());
  const [period,     setPeriod]     = useState<TrafficPeriod>('week');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [connStatus, setConnStatus] = useState<ConnectionStatus>('idle');
  const [errorMsg,   setErrorMsg]   = useState('');
  const [isLive,     setIsLive]     = useState(false);

  // Pastreaza token in ref (nu in state, nu triggereaza re-render)
  const tokenRef      = useRef<string>('');
  const propertyIdRef = useRef<string>('461287871');
  const clientIdRef   = useRef<string>('185237368738-tfmn55decj9hjrr0im5gspvuv79iktsl.apps.googleusercontent.com');

  useEffect(() => {
    blogApi.list().then(setArticles).catch(() => setArticles(defaultArticles));
    productsApi.list().then(setProducts).catch(() => setProducts(defaultProducts));
    seoApi.list().then(setSeoPages).catch(() => setSeoPages(defaultSeoPages));

    // Incarca setarile si incearca conectarea automata daca avem token in sessionStorage
    loadSettings().then(s => {
      propertyIdRef.current = s.propertyId;
      clientIdRef.current   = s.clientId;
      const savedToken = sessionStorage.getItem('ga4_access_token');
      if (savedToken) {
        tokenRef.current = savedToken;
        fetchRealTraffic(savedToken, s.propertyId);
      }
    });
  }, []);

  // Actualizeaza live count la fiecare 30s cand suntem conectati
  useEffect(() => {
    const interval = setInterval(() => {
      if (isLive && tokenRef.current) {
        ga4RunRealtime(propertyIdRef.current, tokenRef.current)
          .then(live => setTraffic(t => ({ ...t, live })))
          .catch(() => {});
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [isLive]);

  const fetchRealTraffic = useCallback(async (token: string, propertyId: string) => {
    setConnStatus('connecting');
    setErrorMsg('');
    try {
      const snapshot = await fetchGA4Traffic(propertyId, token);
      setTraffic(snapshot);
      setLastUpdate(new Date());
      setConnStatus('live');
      setIsLive(true);
    } catch (e: any) {
      const msg = e?.message || 'Eroare GA4';
      setConnStatus('offline');
      setErrorMsg(msg);
      setIsLive(false);
      // Token invalid - sterge din storage
      if (msg.includes('401') || msg.includes('403') || msg.includes('invalid')) {
        sessionStorage.removeItem('ga4_access_token');
        tokenRef.current = '';
      }
    }
  }, []);

  const handleConnect = useCallback(async () => {
    setConnStatus('connecting');
    setErrorMsg('');
    try {
      const token = await requestOAuthToken(
        clientIdRef.current,
        'https://www.googleapis.com/auth/analytics.readonly'
      );
      tokenRef.current = token;
      sessionStorage.setItem('ga4_access_token', token);
      await fetchRealTraffic(token, propertyIdRef.current);
    } catch (e: any) {
      const msg = e?.message || 'Autorizare anulată';
      setConnStatus('offline');
      setErrorMsg(msg);
      setIsLive(false);
    }
  }, [fetchRealTraffic]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    if (isLive && tokenRef.current) {
      await fetchRealTraffic(tokenRef.current, propertyIdRef.current).catch(() => {});
    } else {
      setTraffic(generateDemoData());
      setLastUpdate(new Date());
    }
    setRefreshing(false);
  }, [isLive, fetchRealTraffic]);

  const published   = articles.filter(a => a.status === 'published').length;
  const activeProds = products.filter(p => p.status === 'active').length;

  const stats = [
    { label: 'Articole Blog',     value: articles.length,  sub: `${published} publicate`,  icon: FileText,  color: 'text-blue-400',   bg: 'bg-blue-500/10',   link: '/blog' },
    { label: 'Pagini Site',       value: 18,               sub: 'pagini editabile',          icon: Layout,    color: 'text-purple-400', bg: 'bg-purple-500/10', link: '/pages' },
    { label: 'Produse & Servicii',value: products.length,  sub: `${activeProds} active`,    icon: Package,   color: 'text-green-400',  bg: 'bg-green-500/10',  link: '/products' },
    { label: 'Pagini SEO',        value: seoPages.length,  sub: 'configurate',              icon: Search,    color: 'text-yellow-400', bg: 'bg-yellow-500/10', link: '/seo' },
  ];

  const quickLinks = [
    { label: 'Articol nou',  to: '/blog/new',      icon: Plus,       desc: 'Publică un articol' },
    { label: 'Produs nou',   to: '/products/new',  icon: Plus,       desc: 'Adaugă un produs' },
    { label: 'Setări site',  to: '/settings',      icon: TrendingUp, desc: 'Logo, culori, contact' },
    { label: 'Site live',    href: 'https://interpersonal.ro', icon: Globe, desc: 'Deschide site-ul' },
  ];

  const periodData   = traffic[period] as TrafficPoint[];
  const periodTotV   = periodData.reduce((s, p) => s + p.visitors,  0);
  const periodTotP   = periodData.reduce((s, p) => s + p.pageviews, 0);
  const periodLabels: Record<TrafficPeriod, string> = {
    today: 'Astăzi', week: 'Săptămâna curentă', month: 'Luna curentă', year: 'Anul curent',
  };
  const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-white/40 text-sm mt-1">Bun venit în panoul de administrare Interpersonal IT.</p>
      </div>

      {/* ── Stats ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, sub, icon: Icon, color, bg, link }) => (
          <Link key={label} to={link}
            className="bg-[#07090f] border border-white/10 rounded-xl p-5 hover:border-[#295CA8]/40 transition-all group">
            <div className={`inline-flex p-2.5 rounded-lg ${bg} mb-3`}>
              <Icon size={18} className={color} />
            </div>
            <div className="text-2xl font-bold text-white group-hover:text-[#4ab8ff] transition-colors">{value}</div>
            <div className="text-sm font-medium text-white/70 mt-0.5">{label}</div>
            <div className="text-xs text-white/30 mt-1">{sub}</div>
          </Link>
        ))}
      </div>

      {/* ── TRAFIC WEBSITE ─────────────────────────────────────────── */}
      <div className="rounded-2xl overflow-hidden" style={{ background: '#07090f', border: '1px solid rgba(255,255,255,0.08)' }}>
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#295CA8]/15">
              <BarChart2 size={16} className="text-[#4ab8ff]" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Trafic Website</h2>
              <p className="text-xs text-white/35 mt-0.5">
                {isLive
                  ? 'interpersonal.ro · Date reale Google Analytics 4'
                  : 'interpersonal.ro · Date demo — conectați GA4 pentru live'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Live indicator */}
            {connStatus === 'live' && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}>
                <LiveDot />
                <span className="text-green-400">{traffic.live} activi acum</span>
              </div>
            )}

            {/* Refresh */}
            <button onClick={handleRefresh}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-white/40 hover:text-white hover:bg-white/5 transition-all border border-white/8">
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              {lastUpdate.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
            </button>
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-white/6">
          {[
            { label: 'Vizitatori (30 zile)', value: fmt(traffic.totals.visitors),  icon: Users,    color: '#4ab8ff' },
            { label: 'Pagini afișate',        value: fmt(traffic.totals.pageviews), icon: Eye,      color: '#8b5cf6' },
            { label: 'Durată medie sesiune',  value: traffic.totals.avgSession,     icon: Clock,    color: '#f59e0b' },
            { label: 'Rată respingere',       value: traffic.totals.bounceRate,     icon: Activity, color: '#10b981' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="px-5 py-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Icon size={13} style={{ color }} />
                <span className="text-[10px] text-white/35 uppercase tracking-wide font-medium">{label}</span>
              </div>
              <div className="text-2xl font-bold text-white font-mono">{value}</div>
              {!isLive && <div className="text-[9px] text-white/20 mt-0.5">demo</div>}
            </div>
          ))}
        </div>

        {/* Period selector + chart */}
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-4">
              <span className="text-xs text-white/50">{periodLabels[period]}</span>
              <div className="flex items-center gap-3 text-xs text-white/40">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2 rounded-sm bg-[#4ab8ff] opacity-75"/>
                  Vizitatori: <strong className="text-white/70">{fmt(periodTotV)}</strong>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2 rounded-sm bg-[#8b5cf6] opacity-65"/>
                  Vizualizări: <strong className="text-white/70">{fmt(periodTotP)}</strong>
                </span>
              </div>
            </div>

            {/* Tabs perioadă */}
            <div className="flex border border-white/10 rounded-lg overflow-hidden">
              {(['today', 'week', 'month', 'year'] as TrafficPeriod[]).map(p => (
                <button key={p} onClick={() => setPeriod(p)}
                  className={`px-3 py-1.5 text-xs font-medium transition-all ${
                    period === p ? 'bg-[#295CA8] text-white' : 'text-white/40 hover:text-white hover:bg-white/5'
                  } ${p !== 'today' ? 'border-l border-white/10' : ''}`}>
                  {{ today: 'Azi', week: 'Săptămână', month: 'Lună', year: 'An' }[p]}
                </button>
              ))}
            </div>
          </div>

          {/* Grafic bare */}
          <div className="relative h-[72px]">
            {periodData.length > 0
              ? <MiniBarChart data={periodData} />
              : <div className="h-full flex items-center justify-center text-xs text-white/20">
                  {connStatus === 'connecting'
                    ? 'Se încarcă datele GA4...'
                    : 'Nu există date pentru perioada selectată'}
                </div>
            }
            <div className="absolute inset-0 pointer-events-none"
              style={{ borderTop: '1px solid rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}/>
          </div>

          {/* Labels axă X */}
          {periodData.length > 1 && (
            <div className="flex justify-between mt-1.5 text-[10px] text-white/25">
              <span>{periodData[0].label}</span>
              <span>{periodData[Math.floor(periodData.length / 2)]?.label}</span>
              <span>{periodData[periodData.length - 1].label}</span>
            </div>
          )}
        </div>

        {/* Footer — status GA4 + buton conectare */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-white/6">
          <div className="flex items-center gap-2 text-xs text-white/25">
            {isLive
              ? <><Wifi size={11} className="text-green-400" /> Date reale · GA4 Property {propertyIdRef.current}</>
              : <><AlertCircle size={11} className="text-white/20" /> Date demo · Conectați GA4 pentru trafic real</>
            }
          </div>

          {/* ── INDICATOR CONNECTED LIVE / OffLine ── colț dreapta ── */}
          <ConnectionBadge
            status={connStatus}
            onConnect={handleConnect}
            errorMsg={errorMsg}
          />
        </div>
      </div>

      {/* ── Quick actions ─────────────────────────────────────────── */}
      <div>
        <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-3">Acțiuni rapide</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {quickLinks.map(({ label, to, href, icon: Icon, desc }) => (
            href
              ? <a key={label} href={href} target="_blank" rel="noopener"
                  className="bg-[#07090f] border border-white/10 rounded-xl p-4 hover:border-[#295CA8]/40 transition-all flex items-center gap-3 group">
                  <Icon size={16} className="text-white/40 group-hover:text-[#4ab8ff] transition-colors" />
                  <div><div className="text-sm text-white font-medium">{label}</div><div className="text-xs text-white/30">{desc}</div></div>
                </a>
              : <Link key={label} to={to!}
                  className="bg-[#07090f] border border-white/10 rounded-xl p-4 hover:border-[#295CA8]/40 transition-all flex items-center gap-3 group">
                  <Icon size={16} className="text-white/40 group-hover:text-[#4ab8ff] transition-colors" />
                  <div><div className="text-sm text-white font-medium">{label}</div><div className="text-xs text-white/30">{desc}</div></div>
                </Link>
          ))}
        </div>
      </div>

      {/* ── Articole recente ──────────────────────────────────────── */}
      {articles.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider">Articole recente</h2>
            <Link to="/blog" className="text-xs text-[#4ab8ff] hover:underline">Vezi toate →</Link>
          </div>
          <div className="bg-[#07090f] border border-white/10 rounded-xl divide-y divide-white/5 overflow-hidden">
            {articles.slice(0, 4).map(a => (
              <Link key={a.id} to={`/blog/${a.id}`}
                className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/[0.02] transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{a.title}</div>
                  <div className="text-xs text-white/30 mt-0.5">{a.category} · {a.createdAt}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${a.status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                  {a.status === 'published' ? 'Publicat' : 'Draft'}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
